import React from 'react';
import PropTypes from 'prop-types';
import { Converter } from 'showdown';
import { Link } from 'gatsby';

import 'src/css/styles.css';
import { Title, Content, Image } from '../atoms';

const classMap = {
  p: 'text-xs font-light text-gray-600',
  strong: 'text-gray-800',
  a: 'text-blue-600 underline hover:text-blue-700',
};

const bindings = Object.keys(classMap).map((key) => ({
  type: 'output',
  regex: new RegExp(`<${key}(.*)>`, 'g'),
  replace: `<${key} class="${classMap[key]}" $1>`,
}));

const converter = new Converter({
  extensions: [...bindings],
});

function Divider() {
  return <div className="w-full border-t border-gray-200" />;
}

function Column({ className, ...props }) {
  return <div className={`flex flex-col items-center xl:items-start ${className}`} {...props} />;
}

Column.propTypes = {
  className: PropTypes.string,
};

Column.defaultProps = {
  className: null,
};

export function Footer({ lang, contactUs, usefulLinks, copyright, footerImage1, footerImage2 }) {
  const nowYear = new Date().getFullYear();

  return (
    <footer className="mt-20">
      <Divider />
      <div className="w-full py-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 row-gap-16">
        <Column>
          <Title.Xxs uppercase>{contactUs.title}</Title.Xxs>
          {contactUs.subtitle && (
            <Content className="mt-1" converter={converter}>
              {contactUs.subtitle}
            </Content>
          )}
          <div className="flex flex-col mt-2">
            <a href={contactUs.googleMapsUrl} className="mt-2 text-sm hover:underline">
              {contactUs.address.split(',').map((part) => {
                return (
                  <>
                    <span key={part}>{part}</span>
                    <br />
                  </>
                );
              })}
            </a>
            <a href={`to:${contactUs.email}`} className="mt-2 text-sm hover:underline">
              {contactUs.email}
            </a>
            {contactUs.phones.map((phone) => (
              <div key={phone.number} className="flex mt-2 text-sm font-light">
                <div className="text-direction-ltr ">{phone.number}</div>
              </div>
            ))}
          </div>
        </Column>
        <Column>
          <Title.Xxs uppercase>{usefulLinks.title}</Title.Xxs>
          {usefulLinks.subtitle && (
            <Content className="mt-1" converter={converter}>
              {usefulLinks.subtitle}
            </Content>
          )}
          <div className="flex flex-col mt-2">
            {usefulLinks.links.map((link) => (
              <a key={`${link.name}-${link.url}`} href={link.url} className="mt-2 text-sm hover:underline">
                {link.name}
              </a>
            ))}
          </div>
        </Column>
        <Column className="md:col-span-2">
          <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 gap-2 row-gap-16">
            <Column className="justify-center">
              <Image data={footerImage1} className="w-full h-32" imgStyle={{ objectFit: 'contain' }} />
            </Column>
            <Column className="justify-center">
              <Image data={footerImage2} className="w-full h-32" imgStyle={{ objectFit: 'contain' }} />
            </Column>
          </div>
        </Column>
      </div>
      <Divider />
      <div className="flex w-full items-start md:items-center flex-col md:flex-row justify-between w-full py-5 text-xs font-light text-black">
        <div className="flex flex-col md:flex-row">
          <Link to={`/${lang}/terms-and-conditions`} className="text-sm hover:underline">
            Terms and Conditions
          </Link>
          <Link to={`/${lang}/privacy-policy`} className="my-5 md:my-0 md:mx-5 text-sm hover:underline">
            Privacy Policy
          </Link>
        </div>
        <div>
          © 2020 - {nowYear} {copyright.by} <strong>{copyright.organization}</strong>
        </div>
      </div>
    </footer>
  );
}

Footer.propTypes = {
  lang: PropTypes.string.isRequired,
  contactUs: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    address: PropTypes.string.isRequired,
    googleMapsUrl: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phones: PropTypes.arrayOf(
      PropTypes.shape({
        number: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  usefulLinks: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    links: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  footerImage1: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  footerImage2: PropTypes.object.isRequired,
  copyright: PropTypes.shape({
    by: PropTypes.string.isRequired,
    organization: PropTypes.string.isRequired,
  }).isRequired,
};
